/**
 * Consent-gated Meta Pixel (fbevents.js, #2442).
 *
 * Same grant as Google Analytics: `budgit_analytics_consent` must be
 * `granted`, and token-link pages never load it (#1216). Nothing here creates
 * `window.fbq` or inserts the script before that grant. With no (or a
 * non-numeric) `REACT_APP_META_PIXEL_ID` every call is a no-op.
 *
 * Deduplication with the server-side Conversions API
 * (docs/engineering/ad-conversions.md): each conversion is sent with the
 * server's deterministic `event_id` as fbq's `eventID`, so Meta counts a
 * browser + server pair once.
 *   CompleteRegistration → `CompleteRegistration:<sha256(userId)>`
 *   Purchase             → `Purchase:<Stripe invoice id>` (session id when
 *                          Stripe attached no invoice, as on the server)
 * No email, phone, raw user id, or advanced-matching data is passed to fbq.
 */

import { getAnalyticsConsent, subscribeAnalyticsConsent } from './analyticsConsent';
import { isAnalyticsExcludedPath } from '../utils/sensitiveLinkToken';
import {
  getMetaPixelId,
  isMetaPixelConfigured,
  META_EVENTS,
  META_PIXEL_SCRIPT_SRC,
} from './metaPixelConfig';

const FIRED_STORAGE_PREFIX = 'meta_pixel_fired:';

let pixelLoadStarted = false;
let pixelInitialized = false;
let pixelConsentRevoked = false;
const firedKeys = new Set();
/** @type {Map<string, () => boolean>} */
const pendingUntilConsent = new Map();
let consentListenerInstalled = false;

function pathname() {
  if (typeof window === 'undefined' || !window.location) return '';
  return window.location.pathname || '';
}

function consentGranted() {
  return getAnalyticsConsent() === 'granted';
}

export function metaPixelMayLoad() {
  if (typeof window === 'undefined') return false;
  if (!isMetaPixelConfigured()) return false;
  if (!consentGranted()) return false;
  if (isAnalyticsExcludedPath(pathname())) return false;
  return true;
}

/** Meta's base-code queue stub, without the inline script tag. */
function installFbqStub() {
  if (typeof window === 'undefined' || typeof window.fbq === 'function') return;
  const fbq = function fbqStub(...args) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue.push(args);
    }
  };
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;
}

function hasFired(onceKey) {
  if (firedKeys.has(onceKey)) return true;
  try {
    if (window.sessionStorage && window.sessionStorage.getItem(FIRED_STORAGE_PREFIX + onceKey)) {
      firedKeys.add(onceKey);
      return true;
    }
  } catch (_) {
    // Private mode: the in-memory set is the only guard.
  }
  return false;
}

function markFired(onceKey) {
  firedKeys.add(onceKey);
  try {
    if (window.sessionStorage) {
      window.sessionStorage.setItem(FIRED_STORAGE_PREFIX + onceKey, '1');
    }
  } catch (_) {
    // Ignore quota / private mode.
  }
}

/**
 * Load fbevents.js, `init` the pixel and send one PageView. Idempotent.
 * Returns false, and does not touch fbq, until analytics consent is granted.
 * Never throws.
 */
export function enableMetaPixel() {
  if (!metaPixelMayLoad()) return false;
  try {
    installFbqStub();

    if (!pixelLoadStarted) {
      // fbevents.js reads this on load. Without it, every SPA pushState is
      // auto-sent as a PageView; Budgit sends exactly one PageView per load.
      window.fbq.disablePushState = true;
      const existing = document.querySelector(`script[src="${META_PIXEL_SCRIPT_SRC}"]`);
      if (!existing) {
        const script = document.createElement('script');
        script.async = true;
        script.src = META_PIXEL_SCRIPT_SRC;
        document.head.appendChild(script);
      }
      pixelLoadStarted = true;
    }

    if (!pixelInitialized) {
      const pixelId = getMetaPixelId();
      // No automatic button-click / page-metadata events: the pixel sends only
      // the PageView and the conversions below (Privacy, How We Share).
      window.fbq('set', 'autoConfig', false, pixelId);
      window.fbq('init', pixelId);
      window.fbq('track', META_EVENTS.PAGE_VIEW);
      pixelInitialized = true;
    } else if (pixelConsentRevoked) {
      // Re-grant after an in-tab withdrawal: init does not run again.
      window.fbq('consent', 'grant');
      pixelConsentRevoked = false;
    }
    return true;
  } catch (_) {
    return false;
  }
}

/** Boot-time policy: load only if previously granted. */
export function bootMetaPixelFromConsent() {
  if (consentGranted()) {
    enableMetaPixel();
    flushMetaEventsAfterConsent();
  }
}

/**
 * fbq('track', name, params, { eventID }). No-ops before consent (no fbq call).
 * `onceKey` makes a repeated trigger a no-op for the rest of the tab session.
 */
export function trackMetaEvent(eventName, params, { onceKey, eventID } = {}) {
  if (!eventName || typeof window === 'undefined') return false;
  if (onceKey && hasFired(onceKey)) return false;
  if (!enableMetaPixel()) return false;
  if (typeof window.fbq !== 'function') return false;
  try {
    if (eventID) {
      window.fbq('track', eventName, params || {}, { eventID });
    } else {
      window.fbq('track', eventName, params || {});
    }
  } catch (_) {
    return false;
  }
  if (onceKey) markFired(onceKey);
  return true;
}

function queueUntilConsent(onceKey, fire) {
  if (!isMetaPixelConfigured()) return false;
  if (hasFired(onceKey)) return false;
  if (typeof window !== 'undefined' && isAnalyticsExcludedPath(pathname())) return false;
  if (!consentGranted()) {
    // No decision yet: hold the event until Accept. A reject drops it.
    if (getAnalyticsConsent() == null) pendingUntilConsent.set(onceKey, fire);
    return false;
  }
  return fire();
}

/** Fire events that happened before Accept, only after the grant. */
export function flushMetaEventsAfterConsent() {
  if (!metaPixelMayLoad() || pendingUntilConsent.size === 0) return;
  const jobs = Array.from(pendingUntilConsent.values());
  pendingUntilConsent.clear();
  jobs.forEach((fire) => {
    try {
      fire();
    } catch (_) {
      // One event must not block the rest.
    }
  });
}

/** Withdrawal in-tab: the loaded fbevents.js stays resident, so tell it. */
function revokeLoadedPixelConsent() {
  if (!pixelInitialized || pixelConsentRevoked || typeof window.fbq !== 'function') return;
  try {
    window.fbq('consent', 'revoke');
    pixelConsentRevoked = true;
  } catch (_) {
    // Never throw from the consent listener.
  }
}

function installConsentListener() {
  if (consentListenerInstalled || typeof window === 'undefined') return;
  consentListenerInstalled = true;
  subscribeAnalyticsConsent((choice) => {
    if (choice === 'granted') {
      enableMetaPixel();
      flushMetaEventsAfterConsent();
    } else {
      // Reject (or a reset) drops everything that was held for Accept.
      pendingUntilConsent.clear();
      revokeLoadedPixelConsent();
    }
  });
}

installConsentListener();

function toHex(buffer) {
  return Array.from(new Uint8Array(buffer), (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * `CompleteRegistration:<sha256(userId)>` — byte-for-byte the server's
 * `registrationEventId` (backend/services/adConversions/index.js). Resolves
 * null when WebCrypto is unavailable; the raw id is never used instead.
 */
export async function metaRegistrationEventId(userId) {
  const id = userId == null ? '' : String(userId);
  // Budgit user ids are ASCII ObjectIds; anything else is not ours to hash.
  if (!id || !/^[\x20-\x7e]+$/.test(id)) return null;
  const subtle = typeof window !== 'undefined' && window.crypto && window.crypto.subtle;
  if (!subtle || typeof subtle.digest !== 'function') return null;
  const bytes = Uint8Array.from(id, (char) => char.charCodeAt(0));
  const digest = await subtle.digest('SHA-256', bytes);
  return `${META_EVENTS.COMPLETE_REGISTRATION}:${toHex(digest)}`;
}

/** `Purchase:<invoice id>`, falling back to the session id like the server. */
export function metaPurchaseEventId({ invoiceId, sessionId } = {}) {
  const id = (typeof invoiceId === 'string' && invoiceId) || (typeof sessionId === 'string' && sessionId) || '';
  return id ? `${META_EVENTS.PURCHASE}:${id}` : '';
}

/**
 * New web account finished PIN setup. Resolves true when the event was sent
 * now; held events resolve false and fire on Accept. Never rejects.
 */
export async function trackMetaWebSignup(userId) {
  try {
    if (!isMetaPixelConfigured() || hasFired('signup')) return false;
    const eventID = await metaRegistrationEventId(userId);
    if (!eventID) return false;
    return queueUntilConsent('signup', () => trackMetaEvent(
      META_EVENTS.COMPLETE_REGISTRATION,
      {},
      { onceKey: 'signup', eventID }
    ));
  } catch (_) {
    return false;
  }
}

/**
 * Stripe verify-session succeeded. `amountTotalCents` is Stripe's
 * amount_total (smallest currency unit); value and currency are omitted when
 * either is missing. A $0 checkout is a trial start, which the server reports
 * as StartTrial rather than Purchase, so the pixel sends nothing for it.
 */
export function trackMetaPurchase({ amountTotalCents, currency, invoiceId, sessionId } = {}) {
  try {
    const eventID = metaPurchaseEventId({ invoiceId, sessionId });
    if (!eventID) return false;
    const amountMissing = amountTotalCents === null || amountTotalCents === undefined || amountTotalCents === '';
    const cents = amountMissing ? Number.NaN : Number(amountTotalCents);
    if (cents === 0) return false;
    const params = {};
    const code = typeof currency === 'string' && currency ? currency.toUpperCase() : '';
    if (Number.isFinite(cents) && code) {
      params.value = cents / 100;
      params.currency = code;
    }
    const onceKey = `purchase:${eventID}`;
    return queueUntilConsent(onceKey, () => trackMetaEvent(
      META_EVENTS.PURCHASE,
      params,
      { onceKey, eventID }
    ));
  } catch (_) {
    return false;
  }
}

export function resetMetaPixelForTests() {
  pixelLoadStarted = false;
  pixelInitialized = false;
  pixelConsentRevoked = false;
  firedKeys.clear();
  pendingUntilConsent.clear();
  if (typeof window !== 'undefined') {
    delete window.fbq;
    delete window._fbq;
  }
}
